import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { fetchBonusTypes } from '../../database/databaseBonus';

export default function AdminConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);

  const [cancelCutoffHours, setCancelCutoffHours] = useState(4);
  const [reserveDaysAhead, setReserveDaysAhead] = useState(14);
  const [trialPaymentUrl, setTrialPaymentUrl] = useState('');
  const [trialBonusTypeId, setTrialBonusTypeId] = useState('');
  const [calMinMorning, setCalMinMorning] = useState(8);
  const [calMaxMorning, setCalMaxMorning] = useState(13);
  const [calMinAfternoon, setCalMinAfternoon] = useState(17);
  const [calMaxAfternoon, setCalMaxAfternoon] = useState(21);
  const [bonusTypes, setBonusTypes] = useState([]);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);

      const { data: u } = await supabase.auth.getUser();
      const authedUser = u?.user ?? null;
      setUser(authedUser);

      if (authedUser) {
        const { data: prof, error: profErr } = await supabase
          .from('profiles')
          .select('type')
          .eq('id', authedUser.id)
          .single();

        if (profErr) setErr('No se pudo obtener el rol del usuario.');
        else setRole(prof?.type ?? null);
      }

      const [settingsResult, bonusTypesData] = await Promise.all([
        supabase.from('settings').select('*').eq('id', 1).maybeSingle(),
        fetchBonusTypes(),
      ]);

      if (settingsResult.error) {
        setErr('No se pudieron cargar las configuraciones.');
      } else if (settingsResult.data) {
        setCancelCutoffHours(settingsResult.data.cancel_cutoff_hours ?? 4);
        setReserveDaysAhead(settingsResult.data.reserve_days_ahead ?? 14);
        setTrialPaymentUrl(settingsResult.data.trial_class_payment_url ?? '');
        setTrialBonusTypeId(settingsResult.data.trial_bonus_type_id ?? '');
        setCalMinMorning(settingsResult.data.calendar_min_morning ?? 8);
        setCalMaxMorning(settingsResult.data.calendar_max_morning ?? 13);
        setCalMinAfternoon(settingsResult.data.calendar_min_afternoon ?? 17);
        setCalMaxAfternoon(settingsResult.data.calendar_max_afternoon ?? 21);
      }

      setBonusTypes(bonusTypesData || []);
      setLoading(false);
    })();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    setErr(null);

    const cutoff = Number(cancelCutoffHours);
    const days = Number(reserveDaysAhead);
    if (Number.isNaN(cutoff) || cutoff < 0) {
      setErr('Las horas para cancelar deben ser un número mayor o igual a 0.');
      setSaving(false);
      return;
    }
    if (Number.isNaN(days) || days < 0) {
      setErr('Los días para reservar deben ser un número mayor o igual a 0.');
      setSaving(false);
      return;
    }

    const payload = {
      id: 1,
      cancel_cutoff_hours: cutoff,
      reserve_days_ahead: days,
      trial_class_payment_url: trialPaymentUrl || null,
      trial_bonus_type_id: trialBonusTypeId ? Number(trialBonusTypeId) : null,
      calendar_min_morning: Number(calMinMorning),
      calendar_max_morning: Number(calMaxMorning),
      calendar_min_afternoon: Number(calMinAfternoon),
      calendar_max_afternoon: Number(calMaxAfternoon),
      updated_by: user?.id ?? null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await supabase.from('settings').upsert(payload).eq('id', 1);
    if (error) {
      setErr(`No se pudieron guardar los cambios: ${error.message}`);
    } else {
      setMsg('Configuración guardada correctamente.');
    }
    setSaving(false);
  };

  const isAdmin = role === 'admin';

  return (
    <section className="p-6 max-w-2xl mx-auto">
      {loading ? (
        <p className="text-ink/70">Cargando…</p>
      ) : (
        <>
          {!isAdmin && (
            <div className="mb-4 rounded border border-yellow-400 bg-yellow-50 px-3 py-2 text-yellow-800">
              Solo los administradores pueden modificar esta configuración. (Lectura)
            </div>
          )}

          {err && (
            <div className="mb-4 rounded border border-red-400 bg-red-50 px-3 py-2 text-red-700">
              {err}
            </div>
          )}
          {msg && (
            <div className="mb-4 rounded border border-green-400 bg-green-50 px-3 py-2 text-green-700">
              {msg}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1">
                Horas previas para cancelar una clase
              </label>
              <input
                type="number"
                min={0}
                value={cancelCutoffHours}
                onChange={(e) => setCancelCutoffHours(e.target.value)}
                className="w-40 rounded border border-sand bg-cream text-ink px-3 py-2"
                disabled={!isAdmin}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Días de antelación para reservar una clase
              </label>
              <input
                type="number"
                min={0}
                value={reserveDaysAhead}
                onChange={(e) => setReserveDaysAhead(e.target.value)}
                className="w-40 rounded border border-sand bg-cream text-ink px-3 py-2"
                disabled={!isAdmin}
              />
            </div>

            <hr className="border-sand" />

            <div>
              <label className="block text-sm font-medium mb-1">
                Link de pago para clase de prueba
              </label>
              <input
                type="url"
                value={trialPaymentUrl}
                onChange={(e) => setTrialPaymentUrl(e.target.value)}
                placeholder="https://..."
                className="w-full rounded border border-sand bg-cream text-ink px-3 py-2"
                disabled={!isAdmin}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Tipo de bono para clase de prueba
              </label>
              <select
                value={trialBonusTypeId}
                onChange={(e) => setTrialBonusTypeId(e.target.value)}
                className="w-full rounded border border-sand bg-cream text-ink px-3 py-2"
                disabled={!isAdmin}
              >
                <option value="">Sin asignar</option>
                {bonusTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <hr className="border-sand" />

            <div>
              <p className="text-sm font-semibold mb-3 text-ink/80">Horario visible en el calendario</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Inicio mañana (hora)</label>
                  <input type="number" min={0} max={23} value={calMinMorning} onChange={(e) => setCalMinMorning(e.target.value)} className="w-24 rounded border border-sand bg-cream text-ink px-3 py-2" disabled={!isAdmin} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fin mañana (hora)</label>
                  <input type="number" min={0} max={23} value={calMaxMorning} onChange={(e) => setCalMaxMorning(e.target.value)} className="w-24 rounded border border-sand bg-cream text-ink px-3 py-2" disabled={!isAdmin} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Inicio tarde (hora)</label>
                  <input type="number" min={0} max={23} value={calMinAfternoon} onChange={(e) => setCalMinAfternoon(e.target.value)} className="w-24 rounded border border-sand bg-cream text-ink px-3 py-2" disabled={!isAdmin} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fin tarde (hora)</label>
                  <input type="number" min={0} max={23} value={calMaxAfternoon} onChange={(e) => setCalMaxAfternoon(e.target.value)} className="w-24 rounded border border-sand bg-cream text-ink px-3 py-2" disabled={!isAdmin} />
                </div>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={!isAdmin || saving}
                className={`rounded px-4 py-2 text-cream ${
                  isAdmin
                    ? 'bg-terracotta hover:bg-terracotta-dark'
                    : 'bg-ink/30 cursor-not-allowed'
                }`}
              >
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
